import { useCallback, useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebaseClient";
import { logError } from "../lib/logger";
import { formatDraftTimestamp } from "../lib/commandCenterDealDraft";
import { toTaskQueueLoadDisplayError } from "../lib/taskQueueLoadErrors";

const TASK_COLLECTION = "inventory_cleanup_tasks";

export function sortInventoryCleanupTasksByNewest(tasks) {
  return [...tasks].sort((a, b) => {
    const aMs = a.created_at?.toDate?.()?.getTime?.() || Date.parse(a.created_at) || 0;
    const bMs = b.created_at?.toDate?.()?.getTime?.() || Date.parse(b.created_at) || 0;
    return bMs - aMs;
  });
}

/**
 * Load Command Center inventory cleanup tasks for the signed-in admin/buyer.
 */
export function useInventoryCleanupTasks(userId, enabled) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadTasks = useCallback(async () => {
    if (!enabled || !userId) {
      setTasks([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const q = query(
        collection(db, TASK_COLLECTION),
        where("source", "==", "command_center"),
        where("task_type", "==", "expired_inventory_cleanup"),
        where("created_by_uid", "==", userId)
      );
      const snap = await getDocs(q);
      const items = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setTasks(sortInventoryCleanupTasksByNewest(items));
    } catch (err) {
      logError("Failed to load inventory cleanup tasks", err);
      setError(toTaskQueueLoadDisplayError(err, { context: "inventory_cleanup_tasks" }));
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, userId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  return {
    tasks,
    loading,
    error,
    reloadTasks: loadTasks,
    formatTaskTimestamp: formatDraftTimestamp,
  };
}
